import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { Flame, CheckCircle2, XCircle, Award } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyCertificate() {
  const { certId } = useParams();
  useDocumentMeta({
    title: `Verify credential ${certId}`,
    description: "Public verification page for an APM Fire Alarm Exam Prep credential. Confirm the student's name, credential, score, and issue date.",
    path: `/verify/${certId}`,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/verify/${certId}`);
        setData(data);
      } catch {
        setData({ valid: false, message: "Verification service unavailable" });
      } finally {
        setLoading(false);
      }
    })();
  }, [certId]);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-2.5">
          <div className="w-8 h-8 bg-red-600 grid place-items-center">
            <Flame className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[15px] font-bold tracking-tight">APM Fire Alarm Exam Prep</div>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">Credential verification</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16" data-testid="verify-page">
        <div className="overline mb-2"><span className="red-bar" />Certificate ID</div>
        <div className="font-mono text-2xl font-bold text-zinc-950 mb-8 tracking-wide" data-testid="verify-cert-id">
          {certId}
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono">Verifying…</div>
        ) : data?.valid ? (
          <div className="card-tech p-8 border-green-300 bg-green-50/40" data-testid="verify-valid">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-7 h-7 text-green-600" />
              <div className="font-heading text-2xl font-bold tracking-tight">Authentic credential</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <div className="overline">Student</div>
                <div className="font-heading text-lg font-bold" data-testid="verify-student-name">{data.user_name}</div>
              </div>
              <div>
                <div className="overline">Credential</div>
                <div className="font-heading text-lg font-bold flex items-center gap-2">
                  <Award className="w-4 h-4 text-red-600" /> {data.credential}
                </div>
              </div>
              <div>
                <div className="overline">Score</div>
                <div className="font-heading text-lg font-bold">{data.percentage}%</div>
              </div>
              <div>
                <div className="overline">Issued</div>
                <div className="font-heading text-lg font-bold">{new Date(data.issued_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-500 font-mono mt-6 leading-relaxed">
              This credential was issued by APM Fire Alarm Exam Prep after the named student successfully passed the
              NICET Level 1 Foundation practice exam (≥ 70%). Verification timestamp:{" "}
              {new Date().toISOString()}
            </div>
          </div>
        ) : (
          <div className="card-tech p-8 border-red-300 bg-red-50/40" data-testid="verify-invalid">
            <div className="flex items-center gap-3 mb-3">
              <XCircle className="w-7 h-7 text-red-600" />
              <div className="font-heading text-2xl font-bold tracking-tight">Could not verify</div>
            </div>
            <p className="text-zinc-600">
              {data?.message || "No record of that certificate ID was found in our system."}
            </p>
          </div>
        )}

        <Link to="/" className="text-sm text-zinc-600 hover:text-red-600 inline-block mt-10" data-testid="verify-home">
          ← APM home
        </Link>
      </main>
    </div>
  );
}
